import React, { useState, useEffect } from 'react';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Download, Search, Filter, RefreshCw } from 'lucide-react';
import { auth } from '../../services/firebase/config';

interface Redemption {
  id: string;
  source: string;
  campaignId: string;
  userId: string;
  redeemedAt: string;
  tier: string;
}

export const ActivationRedemptionsPage: React.FC = () => {
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [tierFilter, setTierFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchRedemptions();
  }, []);

  const fetchRedemptions = async () => {
    setLoading(true);
    setError('');
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch('/api/activation-codes/redemptions?format=json', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch redemptions');

      const data = await response.json();
      setRedemptions(data.redemptions || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredRedemptions = redemptions.filter(r => {
    const matchesTier = !tierFilter || r.tier === tierFilter;
    const matchesCampaign = !campaignFilter || r.campaignId.toLowerCase().includes(campaignFilter.toLowerCase());
    const matchesSearch = !searchQuery || 
      r.userId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.id.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTier && matchesCampaign && matchesSearch;
  });

  const handleDownloadCsv = () => {
    const headers = ['Code ID', 'Tier', 'Campaign ID', 'User ID', 'Redeemed At', 'Source'];
    const rows = filteredRedemptions.map(r => [
      r.id,
      r.tier,
      r.campaignId,
      r.userId,
      r.redeemedAt,
      r.source
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `redemptions_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const uniqueTiers = Array.from(new Set(redemptions.map(r => r.tier).filter(Boolean))).map(t => ({
    value: t,
    label: t.charAt(0).toUpperCase() + t.slice(1).replace(/_/g, ' ')
  }));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Redemption History</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchRedemptions}
            disabled={loading}
            icon={RefreshCw}
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            onClick={handleDownloadCsv}
            disabled={loading || filteredRedemptions.length === 0}
            icon={Download}
          >
            Download CSV
          </Button>
        </div>
      </div>

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Input
            placeholder="Search User ID or Code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={Search}
            fullWidth
          />
          
          <Select
            options={[{ value: '', label: 'All Tiers' }, ...uniqueTiers]}
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
          />

          <Input
            placeholder="Filter by Campaign ID..."
            value={campaignFilter}
            onChange={(e) => setCampaignFilter(e.target.value)}
            icon={Filter}
            fullWidth
          />
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-md">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Redeemed At</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : filteredRedemptions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    No redemptions found matching your filters.
                  </td>
                </tr>
              ) : (
                filteredRedemptions.map((redemption) => (
                  <tr key={redemption.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {redemption.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                      {redemption.tier.replace(/_/g, ' ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {redemption.campaignId || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                      {redemption.userId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {redemption.redeemedAt ? new Date(redemption.redeemedAt).toLocaleDateString() + ' ' + new Date(redemption.redeemedAt).toLocaleTimeString() : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        <div className="mt-4 text-sm text-gray-500 text-right">
          Showing {filteredRedemptions.length} of {redemptions.length} redemptions
        </div>
      </Card>
    </div>
  );
};
